import { useEffect, useRef, useState } from 'react';

interface Options {
  from?: number;
  to: number;
  duration?: number; // ms
  decimals?: number;
}

export function useAnimatedCounter({ from = 0, to, duration = 1200, decimals = 0 }: Options) {
  const [value, setValue] = useState(from);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    startRef.current = null;

    const animate = (timestamp: number) => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(parseFloat((from + (to - from) * eased).toFixed(decimals)));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [from, to, duration, decimals]);

  return value;
}
